// Shared client-side session helpers.
//
// The JWT itself now lives only in an httpOnly cookie set by the server —
// client JS can never read or clear it directly (that's the whole point:
// an XSS payload can no longer exfiltrate it the way it could when it sat
// in localStorage). Alongside it, the server sets a small non-sensitive
// "hint" cookie (`isLoggedIn` / `isAdmin`, value = the session's expiry as
// an epoch-ms timestamp) that JS CAN read, purely so the UI can render
// logged-in/out state synchronously and auto-logout at the right time.
// That hint grants no access on its own — every real API call is still
// authorized server-side off the httpOnly cookie.

import getApiBaseUrl from "./api";

export const AUTH_EVENT = "psyx-auth-changed";

function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function cookieExpiryMs(name) {
  const raw = readCookie(name);
  const ms = raw ? Number(raw) : NaN;
  return Number.isFinite(ms) ? ms : null;
}

export function getSessionExpiryMs() {
  return cookieExpiryMs("isLoggedIn");
}

export function isSessionActive() {
  const expiryMs = getSessionExpiryMs();
  return !!expiryMs && expiryMs > Date.now();
}

export function getAdminSessionExpiryMs() {
  return cookieExpiryMs("isAdmin");
}

export function isAdminSessionActive() {
  const expiryMs = getAdminSessionExpiryMs();
  return !!expiryMs && expiryMs > Date.now();
}

// Ends the real (user) session: asks the server to clear the httpOnly
// cookie, clears the local hint cookie, and notifies the rest of the app.
export async function clearSession() {
  try {
    await fetch(`${getApiBaseUrl()}/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // Even if the network call fails, forget the session locally.
  }
  document.cookie = "isLoggedIn=; Max-Age=0; path=/";
  window.dispatchEvent(new Event(AUTH_EVENT));
}

export async function clearAdminSession() {
  try {
    await fetch(`${getApiBaseUrl()}/auth/admin/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // Even if the network call fails, forget the session locally.
  }
  document.cookie = "isAdmin=; Max-Age=0; path=/";
  window.dispatchEvent(new Event(AUTH_EVENT));
}
